import { useState } from 'react';
import { FlashbarProps } from '@cloudscape-design/components';

// Extended notification type that includes dismissTimeout
interface ExtendedMessageDefinition extends FlashbarProps.MessageDefinition {
  dismissTimeout?: number;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<ExtendedMessageDefinition[]>([]);

  /**
   * Add a notification to the flashbar
   */
  const addNotification = (notification: ExtendedMessageDefinition) => {
    // Create a unique ID if not provided
    const id = notification.id || `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add a dismissible property and onDismiss handler if not provided
    const enhancedNotification = {
      ...notification,
      id,
      dismissible: notification.dismissible !== false,
      onDismiss: notification.onDismiss || (() => removeNotification(id))
    };
    
    // Add the notification to the list
    setNotifications(current => [...current, enhancedNotification]);
    
    // If it has a dismissTimeout, remove it after the specified time
    if (notification.dismissTimeout) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.dismissTimeout);
    }
    
    return id;
  };

  /**
   * Remove a notification by ID
   */
  const removeNotification = (id: string) => {
    setNotifications(current => current.filter(notification => notification.id !== id));
  };

  /**
   * Add a success notification
   */
  const addSuccessNotification = (content: React.ReactNode, title?: string) => {
    return addNotification({
      type: 'success',
      content,
      header: title,
      dismissible: true,
      dismissTimeout: 5000
    });
  };

  /**
   * Add an error notification
   */
  const addErrorNotification = (content: React.ReactNode, title?: string) => {
    return addNotification({
      type: 'error',
      content,
      header: title || 'Error',
      dismissible: true
    });
  };

  /**
   * Add an info notification
   */
  const addInfoNotification = (content: React.ReactNode, title?: string) => {
    return addNotification({
      type: 'info',
      content,
      header: title,
      dismissible: true,
      dismissTimeout: 7000
    });
  };

  // For AppLayout, we need to provide the notifications in the correct format
  const flashbarItems = notifications.length > 0 ? notifications : undefined;

  return {
    notifications: flashbarItems,
    addNotification,
    removeNotification,
    addSuccessNotification,
    addErrorNotification,
    addInfoNotification
  };
}
